<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import useSettingStore from '../stores/setting';
import useMediaStore from '../stores/media';
import { Track } from '../types';
import useRenditionStore from '../stores/rendition';
import useReaderEngine from '../engine/use-reader-engine';
import { addSentryBreadcrumb, captureSentryMessage } from '@ilteducation/sentry-vue3';
import useAudioAnnotations from '../composables/use-audio-annotations';

const DEFAULT_VIDEO_FOLDER = 'pg_video';
const VIDEO_EXTENSION = '.mp4';

type Emits = {
  (e: 'setVideoProps', track: Track): void;
  (e: 'videoAspect', aspect: number): void;
};
const emit = defineEmits<Emits>();

const mediaPlayer = ref<HTMLVideoElement>();
const videoActive = ref<boolean>(false);
const isUsingFallbackUrl = ref<boolean>(false);
const hasReportedFallback = ref<boolean>(false);

const { getBookResourceUrl } = useReaderEngine();
const { hasAudioTracking, hasAutoPageTurn } = storeToRefs(useSettingStore());
const { playbackRate, isMuted, audioVolume } = storeToRefs(useSettingStore());
const mediaStore = useMediaStore();
const {
  currentPageTracks,
  isPlaying,
  playing,
  currentTrack,
  getNextTrackOnPage,
  getUniqueNextTrackOnPage,
  mainLanguage,
  isVideoLanguage,
  pictureInPicture,
  audioLanguage,
  lastPlayedBeginTime,
  isCurrentTrackOnCurrentPage,
  isUserChangingTrack,
} = storeToRefs(mediaStore);
const {
  switchToTheNextPage,
  setFirstTrackInPageAsCurrent,
  setCurrentTrack,
  setLastPlayedBeginTime,
  setIsUserChangingTrack,
} = mediaStore;
const renditionStore = useRenditionStore();
const { isChangingPage, isReflowable, currentPageFilesWithTracks, rendition } = storeToRefs(renditionStore);
const { goToNextSpine } = useRenditionStore();
const { highlightPlayingTrack, clearPlayingHighlight } = useAudioAnnotations();

const setCurrentTrackTime = (trackTime: number) => {
  if (mediaPlayer.value) {
    mediaPlayer.value.currentTime = trackTime;
  }
};

const resetPlayerTime = () => {
  const clipBegin =
    currentTrack.value && currentTrack.value.audio && currentTrack.value.audio.clipBegin !== null
      ? currentTrack.value.audio.clipBegin
      : 0;

  setCurrentTrackTime(clipBegin);
};

// @ts-ignore - TODO: Document this and Make this clear
const getFileName = (track: Track) => track.audio.src.split('/').pop().split('.')[0];

const unique = (value: any, index: number, self: any) => self.indexOf(value) === index;

const isVideoFileEnded = () => mediaPlayer.value!.currentTime >= mediaPlayer.value!.duration;

/*
 Derived, not assigned. Switching audio language flips `hasPictureInPicture`, which swaps the
 v-if/v-else branches in MediaPlayer and remounts this component - an assigned ref comes back
 `undefined` and nothing ever recomputes it, so the <video> ends up with no source at all.
 */
const videoUrl = computed(() => {
  if (!currentTrack.value || currentPageTracks.value.length === 0) {
    return undefined;
  }

  let videoFileName = '';

  if (currentPageTracks.value.length === 1) {
    videoFileName = getFileName(currentPageTracks.value[0]);
  } else if (pictureInPicture.value.length !== 0) {
    videoFileName = getFileName(currentTrack.value);
  } else {
    videoFileName = currentPageTracks.value.map(getFileName).filter(unique).join('_');
  }

  return getBookResourceUrl(`${DEFAULT_VIDEO_FOLDER}/${audioLanguage.value}/${videoFileName}${VIDEO_EXTENSION}`);
});

/*
 Video files are addressed by convention - `pg_video` is not in the manifest, so the name is
 rebuilt from the SMIL audio track and `.mp4` is appended. Ingest copies epub entry names
 verbatim, and some epubs ship these files without an extension, so the object in the bucket is
 `pg_video/takk/006`, not `006.mp4`. Those books 403 on the first try; drop the extension and
 retry once.

 One retry, no probing. If a third naming convention ever turns up, put the real
 video paths in the manifest rather than adding another guess here.
 */
const videoSrc = computed(() =>
  isUsingFallbackUrl.value ? videoUrl.value?.slice(0, -VIDEO_EXTENSION.length) : videoUrl.value,
);

watch(videoUrl, () => {
  isUsingFallbackUrl.value = false;
});

const play = () => {
  if (mediaPlayer.value) {
    mediaPlayer.value.play().catch((error: any) => {
      console.error(error);
    });
  }
};

const pause = () => {
  if (mediaPlayer.value) {
    mediaPlayer.value.pause();
  }
};

const togglePlay = (toggle: boolean) => {
  if (toggle) {
    if (currentTrack.value) {
      console.debug(`Playing media`);
      play();
    } else {
      switchToTheNextPage();
    }
  } else {
    console.debug(`Pausing media`);
    pause();
  }
};

const onTrackFinished = () => {
  let nextTrackOnPage;

  if (mainLanguage.value === audioLanguage.value) {
    nextTrackOnPage = getNextTrackOnPage.value;
  } else if (!isVideoLanguage.value || pictureInPicture.value.length !== 0) {
    nextTrackOnPage = getUniqueNextTrackOnPage.value;
  }

  if (nextTrackOnPage) {
    console.debug('Going to next track on page');
    setCurrentTrack(nextTrackOnPage);
  } else if (hasAutoPageTurn.value) {
    console.debug(`No more media tracks on current page(s), going to next spine`);
    videoActive.value = false;
    mediaPlayer.value?.pause();
    goToNextSpine();
  } else if (isPlaying.value) {
    console.debug(`Continue playing: ${isPlaying.value}`);
    setCurrentTrack(currentPageTracks.value[0]);
    resetPlayerTime();
    setLastPlayedBeginTime(currentPageTracks.value[currentPageTracks.value.length - 1].audio.clipBegin || 0);
    playing.value = !isPlaying.value;
  }
};

const onTrackEnd = () => {
  console.debug('Track has Ended');
  nextTick(() => {
    if (!isChangingPage.value) {
      onTrackFinished();
    }
  });
};

const clearCurrentTrack = () => {
  if (mediaPlayer.value) {
    mediaPlayer.value.pause();
  }

  setCurrentTrack(undefined);
};

/**
 * Watcher to detect page switching.
 */
watch(currentPageFilesWithTracks, () => {
  if (currentPageTracks.value?.length > 0) {
    console.debug(`${currentPageTracks.value.length} track(s) detected`);
    const detectFirstLoad = !isCurrentTrackOnCurrentPage.value;

    if (isReflowable.value && isChangingPage.value) {
      setFirstTrackInPageAsCurrent();
    }
    if (detectFirstLoad) {
      setFirstTrackInPageAsCurrent();
      resetPlayerTime();
    }
  } else {
    clearCurrentTrack();
    if (isPlaying.value) {
      switchToTheNextPage();
    }
  }
});

/**
 * Callback on time update in the player.
 */
const onTrackUpdate = () => {
  if (mediaPlayer.value) {
    if (currentTrack.value && !isChangingPage.value) {
      if (isVideoFileEnded()) {
        if (mainLanguage.value === audioLanguage.value) {
          console.debug('Reached the end of the clip, switching to the next one.');
          onTrackFinished();
        }
      } else if (
        currentTrack.value.audio.turnPageAt &&
        mediaPlayer.value.currentTime >= currentTrack.value.audio.turnPageAt &&
        hasAutoPageTurn.value
      ) {
        console.debug('Switching to the next spine to keep reading the media.');
        goToNextSpine();
      }
    }
  }
};

const onCurrentTrackChange = async (newTrack: Track, oldTrack?: Track) => {
  await nextTick(() => {
    if (newTrack && currentTrack.value) {
      // Only reset the current playback position if the track was changed by the user
      // (otherwise some part of the media might be played twice)
      if (isUserChangingTrack.value) {
        if (
          lastPlayedBeginTime.value &&
          lastPlayedBeginTime.value === currentTrack.value.audio.clipBegin &&
          currentTrack.value.audio.clipBegin !== null
        ) {
          // Sets the media player current time as clip end to avoid played media twice.
          setCurrentTrackTime(currentTrack.value.audio.clipEnd || 0);
        } else {
          // Sets the media player current time
          setCurrentTrackTime(currentTrack.value.audio.clipBegin || 0);
        }
        setIsUserChangingTrack(false);
      }

      console.debug(`Current video track is ${videoUrl.value} starting at ${currentTrack.value.audio.clipBegin}`);
    } else {
      console.debug('No track to play');
    }
  });
};

// Watcher for audio highlighting (uses epub.js annotations)
watch(
  [currentTrack, hasAudioTracking, mainLanguage, audioLanguage, isPlaying],
  ([track, tracking, main, audio, playing]) => {
    if (!tracking || !rendition.value) {
      // Clear highlight if conditions not met
      if (mediaStore.activeHighlightCfi) {
        clearPlayingHighlight();
      }
      return;
    }

    // Apply highlight when playing, clear when paused
    if (track?.cfi && main === audio && playing) {
      highlightPlayingTrack(track);
    } else if (main === audio && !playing) {
      clearPlayingHighlight();
    }
  },
);

const isVideoActive = computed(() => videoActive.value);

watch(isMuted, (value) => {
  if (mediaPlayer.value) {
    mediaPlayer.value.muted = value;
  }
});

watch(playbackRate, (value) => {
  if (mediaPlayer.value) {
    mediaPlayer.value.playbackRate = value;
  }
});

watch(audioVolume, (value) => {
  if (mediaPlayer.value) {
    mediaPlayer.value.muted = false;
    mediaPlayer.value.volume = value;
  }
});

watch(isPlaying, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    togglePlay(newValue);
  }
});

/** Watcher to detect current media file switching. */
watch(currentTrack, (newTrack, oldTrack) => {
  if (newTrack && newTrack !== oldTrack) {
    onCurrentTrackChange(newTrack, oldTrack).then(() => {
      emit('setVideoProps', newTrack);
    });
  }
});

onMounted(() => {
  mediaPlayer.value?.addEventListener('canplay', () => {
    if (mediaPlayer.value) {
      mediaPlayer.value.playbackRate = playbackRate.value;
      mediaPlayer.value.muted = isMuted.value;
      mediaPlayer.value.volume = audioVolume.value;
    }
    videoActive.value = true;
    togglePlay(isPlaying.value);
  });

  // Binding :src directly means the element reloads itself whenever the URL changes; hide the
  // overlay for that gap so a half-loaded frame of the previous clip is never on screen.
  mediaPlayer.value?.addEventListener('loadstart', () => {
    videoActive.value = false;
  });

  mediaPlayer.value?.addEventListener('error', () => {
    if (!isUsingFallbackUrl.value && videoUrl.value) {
      console.debug(`No video at ${mediaPlayer.value?.currentSrc}, retrying without the extension`);
      addSentryBreadcrumb({
        category: 'book-player.video',
        level: 'warning',
        message: 'No video at the .mp4 path, retrying without the extension',
        data: { url: mediaPlayer.value?.currentSrc },
      });

      /*
       A breadcrumb only ships when some later event captures it, and the retry usually succeeds -
       so without this the mispackaged books would never show up in Sentry at all. One message per
       mount is enough to identify the book from the URL.
       */
      if (!hasReportedFallback.value) {
        hasReportedFallback.value = true;
        captureSentryMessage('Book video files are packaged without a file extension', {
          url: mediaPlayer.value?.currentSrc,
        });
      }

      isUsingFallbackUrl.value = true;
      return;
    }

    console.error(`Failed to load video ${mediaPlayer.value?.currentSrc}`, mediaPlayer.value?.error);
  });

  mediaPlayer.value?.addEventListener('loadedmetadata', () => {
    const w = mediaPlayer.value?.videoWidth ?? 0;
    const h = mediaPlayer.value?.videoHeight ?? 0;
    if (w > 0 && h > 0) emit('videoAspect', w / h);
  });
});
</script>

<template>
  <div class="video-player-overlay" :class="{ active: isVideoActive }">
    <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
    <video ref="mediaPlayer" :src="videoSrc" @timeupdate="onTrackUpdate" @ended="onTrackEnd"></video>
  </div>
</template>

<style scoped lang="scss">
@use 'vuetify/settings' as v;

.video-player-overlay {
  background-color: rgb(var(--v-theme-surface));
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 6; // Above the animated flipping pages. See packages/book-player-vue3/src/util/epubJs/managers/flipper.index.js zIndexes
  display: flex;
  opacity: 0;
  transition: all 0.5s;
}
.video-player-overlay.active {
  opacity: 1;
}
.video-player-overlay video {
  width: 100%;
  max-height: 100%;
  box-sizing: border-box;
}
</style>
